import { useState, useEffect } from 'react';
import { useSubscription } from '@apollo/client';
import { Registration } from '../API';
import { ON_CREATE_REGISTRATION } from '../graphql/subscriptions/onCreateRegistration';
import { ON_UPDATE_REGISTRATION } from '../graphql/subscriptions/onUpdateRegistration';
import { Attendee } from '../components/ZplPrinter';

export function useRegistrationSubscriptions(
  eventId: string | undefined,
  initialRegistrations: Registration[],
  printingEnabled: boolean,
  onPrintTrigger: (attendee: Attendee) => void,
) {
  const [registrations, setRegistrations] = useState<Registration[]>(initialRegistrations);

  const { data: newRegistrationData, error: newRegistrationError } = useSubscription<{ onCreateRegistration: Registration }>(ON_CREATE_REGISTRATION, {
    variables: { eventId },
  });

  const { data: updatedRegistrationData, error: updatedRegistrationError } = useSubscription<{ onUpdateRegistration: Registration }>(ON_UPDATE_REGISTRATION, {
    variables: { eventId },
  });

  useEffect(() => {
    if (newRegistrationError) {
      console.error('Error with new registration subscription:', newRegistrationError);
    }
    if (newRegistrationData) {
      const newRegistration = newRegistrationData.onCreateRegistration;
      setRegistrations(prev => [
        ...prev.filter(r => r.registrationId !== newRegistration.registrationId),
        newRegistration,
      ]);
    }
  }, [newRegistrationData, newRegistrationError]);

  useEffect(() => {
    if (updatedRegistrationError) {
      console.error('Error with updated registration subscription:', updatedRegistrationError);
    }
    if (updatedRegistrationData) {
      const updatedRegistration = updatedRegistrationData.onUpdateRegistration;
      setRegistrations(prev =>
        prev.map(reg => reg.registrationId === updatedRegistration.registrationId ? updatedRegistration : reg)
      );
      if (printingEnabled && updatedRegistration.registrationStatus === 'CHECKED_IN') {
        onPrintTrigger(updatedRegistration);
      }
    }
  }, [updatedRegistrationData, updatedRegistrationError, printingEnabled, onPrintTrigger]);

  return { registrations, setRegistrations };
}
